import debounce from 'lodash/debounce'

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'hooks/use-redux'
import { useParams, useNavigate } from 'react-router-dom'
import type { ClientLibraryId } from 'types/client-library'
import type { Playlist } from 'types/playlist'
import type { State } from 'types/redux'
import emptySearch1x from 'assets/images/empty-search.png'
import emptySearch2x from 'assets/images/empty-search@2x.png'
import { useTranslation } from '@disco/i18n/use-translation'
import { clientLibraryAlbumsUpdate } from '@disco/state-management/actions/client-library-album-actions'
import {
  batchUpdatePlaylists,
  getKeyByPlaylistType,
  loadNextPlaylists,
  searchPlaylists,
} from '@disco/state-management/actions/playlist-actions'
import AlbumGridItem from 'components/common/album-grid-item'
import PlaylistGridItem from 'components/common/playlist-grid-item'
import Row from 'components/common/row'
import DocumentHeadTags from 'components/document-head-tags'
import {
  type AlbumVariant,
  getAlbumLabels,
} from 'components/libraries/library-albums-manager/album-labels'
import { Tab, Tabs } from 'components/ui'
import Button from 'components/ui/button'
import Checkbox from 'components/ui/checkbox'
import EmptyState from 'components/ui/empty-state'
import Grid from 'components/ui/grid'
import Heading from 'components/ui/heading'
import InfiniteScroll from 'components/ui/infinite-scroll'
import Input from 'components/ui/input'
import ModalWindow from 'components/ui/modal-window'

type Props = {
  libraryId?: ClientLibraryId
  variant?: AlbumVariant
}

/**
 * Modal for publishing albums to a catalog, or converting playlists to albums and publishing them.
 * @param libraryId - ID of the catalog; defaults to the `libraryId` route param
 * @param variant - `albums` for catalogs, `releases` for artist profiles
 */
const LibrariesLibraryAlbumsAdd = ({
  libraryId: libraryIdProp,
  variant = 'albums',
}: Props): React.ReactNode => {
  const params = useParams<{ libraryId: string }>()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { t } = useTranslation('catalogs-management')
  const labels = getAlbumLabels(t, variant)
  const libraryId: number = libraryIdProp ?? parseInt(params.libraryId!, 10)
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [selectedTab, setSelectedTab] = useState<number>(0)
  const [query, setQuery] = useState<string>('')
  const [isLoadingNext, setIsLoadingNext] = useState<boolean>(false)
  const [isSearching, setIsSearching] = useState<boolean>(false)
  const playlistType: Playlist['playlist_type'] = useMemo(
    () => (selectedTab === 0 ? 'album' : 'playlist'),
    [selectedTab],
  )
  const search: Record<string, any> = useSelector((state: State) => {
    const key = getKeyByPlaylistType(playlistType)
    return state.clientLibraries.search[key]
  })
  useEffect(() => {
    setIsLoadingNext(false)
    dispatch(searchPlaylists(query, playlistType))
  }, [playlistType, selectedTab])
  const debouncedQuery = useRef(
    debounce((query: string, playlistType: Playlist['playlist_type']) => {
      setIsSearching(false)
      dispatch(searchPlaylists(query, playlistType))
    }, 300),
  ).current
  const updateSearchQuery = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setIsLoadingNext(false)
      setIsSearching(true)
      setQuery(event.target.value)
      debouncedQuery(event.target.value, playlistType)
    },
    [debouncedQuery, playlistType],
  )
  const updateSelectedIds = useCallback((id: number) => {
    setSelectedIds((prevState: number[]) => {
      if (prevState.includes(id)) {
        return prevState.filter((item: number) => item !== id)
      } else {
        return [...prevState, id]
      }
    })
  }, [])
  const loadNext = useCallback(
    (next: string) => {
      setIsLoadingNext(true)
      dispatch(loadNextPlaylists(next, playlistType))
    },
    [dispatch, playlistType],
  )
  const handleCancel = useCallback(
    (event: React.SyntheticEvent<any>) => {
      event.preventDefault()
      navigate(-1)
    },
    [navigate],
  )
  const getSubmitCaption = (): string => {
    if (playlistType === 'album') {
      return selectedIds.length > 0
        ? labels.publishCount(selectedIds.length)
        : labels.publishAll
    }

    if (playlistType === 'playlist') {
      return selectedIds.length > 0
        ? t(
            'catalogs-management:convertAndPublishCountPlaylists',
            'Convert and publish {{count}} playlists',
            {
              count: selectedIds.length,
              defaultValue_one: 'Convert and publish {{count}} playlist',
            },
          )
        : t(
            'catalogs-management:convertAndPublishPlaylists',
            'Convert and publish playlists',
          )
    }

    return ''
  }
  const handleSubmit = useCallback(
    (event: React.SyntheticEvent<any>) => {
      event.preventDefault()
      dispatch(clientLibraryAlbumsUpdate(libraryId, selectedIds, []))

      if (playlistType === 'playlist') {
        dispatch(
          batchUpdatePlaylists(selectedIds, {
            playlist_type: 'album',
          }),
        )
      }

      navigate(-1)
    },
    [dispatch, libraryId, selectedIds, navigate],
  )

  const renderContent = () => {
    if (!isLoadingNext && search.loading) {
      return t('catalogs-management:loading', 'Loading...')
    } else if (
      !isSearching &&
      query !== '' &&
      search.loaded &&
      search.items.length === 0
    ) {
      return (
        <EmptyState>
          <img
            srcSet={`${emptySearch1x}, ${emptySearch2x} 2x`}
            width="177"
            height="160"
          />
          <p>
            {t(
              'catalogs-management:noSearchResults',
              'Sorry! We couldn’t find anything for that search. Please try another query.',
            )}
          </p>
        </EmptyState>
      )
    }

    return (
      <InfiniteScroll
        next={search.next}
        loadNext={loadNext}
        loadingNode={t('catalogs-management:loading', 'Loading...')}
      >
        {playlistType === 'album' && (
          <Grid size="medium">
            {search.items.map((item: Playlist) => {
              const isPublished: boolean = item['client_library_ids']
                ? item['client_library_ids'].includes(libraryId)
                : false
              const isSelected: boolean = selectedIds.includes(
                item['playlist_id'],
              )
              const overlayActions: React.ReactNode = !isPublished && (
                <Checkbox checked={isSelected} />
              )
              return (
                <AlbumGridItem
                  key={item['playlist_id']}
                  album={item}
                  status={
                    isPublished
                      ? t('catalogs-management:published', 'Published')
                      : undefined
                  }
                  overlayActions={overlayActions}
                  overlayVisible={isSelected}
                  onClick={!isPublished ? updateSelectedIds : undefined}
                />
              )
            })}
          </Grid>
        )}
        {playlistType === 'playlist' && (
          <Grid size="large">
            {search.items.map((item: Playlist) => {
              const isSelected: boolean = selectedIds.includes(
                item['playlist_id'],
              )
              const overlayActions: React.ReactNode = (
                <Checkbox checked={isSelected} />
              )
              return (
                <PlaylistGridItem
                  key={item['playlist_id']}
                  playlist={item}
                  overlayActions={overlayActions}
                  overlayVisible={isSelected}
                  onClick={updateSelectedIds}
                />
              )
            })}
          </Grid>
        )}
      </InfiniteScroll>
    )
  }

  return (
    <ModalWindow
      header={
        <>
          <Heading size={700}>{labels.publishNew}</Heading>
          <div className="form-note">{labels.addInstructions}</div>
        </>
      }
      actions={
        <Button
          appearance="primary"
          caption={getSubmitCaption()}
          disabled={search.loading || selectedIds.length === 0}
          onClick={handleSubmit}
        />
      }
      isOpen={true}
      onClose={handleCancel}
    >
      <DocumentHeadTags
        title={t('catalogs-management:titleDisco', '{{title}} | DISCO', {
          title: labels.publishNew,
        })}
      />
      <Row columnsSize={[3]} marginBottom="small">
        <div>
          <Input
            icon="search"
            placeholder={t('catalogs-management:search', 'Search')}
            value={query}
            onChange={updateSearchQuery}
          />
        </div>
      </Row>
      <Tabs
        className="Tabs--modal"
        index={selectedTab}
        onChange={(index: number) => setSelectedTab(index)}
      >
        <Tab label={labels.title}>{renderContent()}</Tab>
        <Tab label={t('catalogs-management:playlists', 'Playlists')}>
          {renderContent()}
        </Tab>
      </Tabs>
    </ModalWindow>
  )
}

export default LibrariesLibraryAlbumsAdd
